import React, { useEffect, useState } from 'react';
import { Modal, Form, Input } from 'antd';
import QuillRichText from 'components/QuillRichText';
import s from './s.module.less';
import commonS from 'styles/common.module.less';
import { TMedication } from '../../../type';

interface MedicationModalProps {
    visible: boolean;
    onCancel: () => void;
    onSave: (data: TMedication) => void;
    initialData?: TMedication;
    medicationName?: string;
}

const MedicationModal: React.FC<MedicationModalProps> = ({
    visible,
    onCancel,
    onSave,
    initialData,
    medicationName = 'Medication',
}) => {
    const [form] = Form.useForm<TMedication>();
    const [charCounts, setCharCounts] = useState({
        name: 0,
        details: 0,
        callToActionName: 0,
    });

    // 更新字符计数
    const updateCharCounts = (data: Partial<TMedication>) => {
        setCharCounts({
            name: (data.name || '').length,
            details: (data.details || '').replace(/<[^>]*>/g, '').length,
            callToActionName: (data.callToActionName || '').length,
        });
    };

    // 当模态框打开或初始数据变化时，设置表单数据
    useEffect(() => {
        if (visible) {
            if (initialData) {
                // 编辑模式：设置所有字段包括priority（虽然不显示）
                form.setFieldsValue({
                    name: initialData.name,
                    details: initialData.details,
                    callToActionName: initialData.callToActionName,
                    callToActionLink: initialData.callToActionLink,
                    priority: initialData.priority, // 保持priority字段
                });
                updateCharCounts(initialData);
            } else {
                // 新增模式：设置默认值
                const defaultValues = {
                    priority: 1,
                    name: '',
                    details: '',
                    callToActionName: '',
                    callToActionLink: '',
                };
                form.setFieldsValue(defaultValues);
                setCharCounts({
                    name: 0,
                    details: 0,
                    callToActionName: 0,
                });
            }
        }
    }, [visible, initialData, form]);

    // 处理表单提交
    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            onSave(values);
        } catch (error) {
            console.error('表单验证失败:', error);
        }
    };

    // 处理取消
    const handleCancel = () => {
        form.resetFields();
        onCancel();
    };

    // 处理字段值变化
    const handleFieldChange = (fieldName: keyof TMedication, value: string) => {
        form.setFieldValue(fieldName, value);

        // 更新字符计数
        const newCounts = { ...charCounts };
        if (fieldName === 'name') {
            newCounts.name = value.length;
        } else if (fieldName === 'details') {
            newCounts.details = value.replace(/<[^>]*>/g, '').length;
        } else if (fieldName === 'callToActionName') {
            newCounts.callToActionName = value.length;
        }
        setCharCounts(newCounts);
    };

    return (
        <Modal
            title={initialData ? `Edit ${medicationName}` : `Add ${medicationName}`}
            className={commonS.modalFixHeightWrap}
            open={visible}
            onCancel={handleCancel}
            okText="Save changes"
            onOk={handleSubmit}
            width={543}
            destroyOnClose
        >
            <Form
                form={form}
                layout="vertical"
                className={s.form}
            >
                {/* Hidden Priority Field - 保持priority值 */}
                <Form.Item name="priority" hidden>
                    <Input type="hidden" />
                </Form.Item>

                {/* Name Field */}
                <Form.Item
                    label="Name"
                    name="name"
                    rules={[
                        { required: true, message: 'Please enter medication name' },
                        { max: 30, message: 'Name cannot exceed 30 characters' },
                    ]}
                >
                    <Input
                        className={s.nameInput}
                        placeholder="Ritalin"
                        maxLength={30}
                        onChange={(e) => handleFieldChange('name', e.target.value)}
                    />
                </Form.Item>

                {/* Details Field */}
                <Form.Item
                    label="Details"
                    name="details"
                    rules={[
                        { required: true, message: 'Please enter medication details' },
                        {
                            validator: (_, value) => {
                                const plainText = value?.replace(/<[^>]*>/g, '') || '';
                                if (plainText.length > 8000) {
                                    return Promise.reject(new Error('Details cannot exceed 8000 characters'));
                                }
                                return Promise.resolve();
                            },
                        },
                    ]}
                >
                    <QuillRichText
                        keepHtmlLabels
                        height="auto"
                        value={form.getFieldValue('details')}
                        plcaeholder="Used for patients with Attention Deficit Hyperactivity Disorder (ADHD). This medication helps improve focus and reduce impulsivity by affecting certain natural substances in the brain."
                        onChange={(value) => handleFieldChange('details', value)}
                        showCount
                        maxLength={300}
                    />
                </Form.Item>

                {/* Call to Action Name Field */}
                <Form.Item
                    label="Call-to-action name"
                    name="callToActionName"
                    rules={[
                        { required: true, message: 'Please enter call-to-action name' },
                        { max: 20, message: 'Call-to-action name cannot exceed 20 characters' },
                    ]}
                >
                    <Input
                        className={s.callToActionNameInput}
                        placeholder="Learn more"
                        maxLength={20}
                        onChange={(e) => handleFieldChange('callToActionName', e.target.value)}
                    />
                </Form.Item>

                {/* Call to Action Link Field */}
                <Form.Item
                    label="Call-to-action link *"
                    name="callToActionLink"
                    rules={[
                        { required: true, message: 'Please enter call-to-action link' },
                        { type: 'url', message: 'Please enter a valid URL' },
                    ]}
                >
                    <Input
                        className={s.callToActionLinkInput}
                        placeholder="https://www.example.com/med1"
                    />
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default MedicationModal;
